import {
  putV1CompaniesCompanyIdAiReviewsIdDismiss,
  putV1CompaniesCompanyIdAiReviewsIdUndismiss,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { AiReview } from '~hooks/useAiReview/models';
import connection from '~services/connectionModule';

interface AiReviewDismissalArgs {
  aiReviewId: string;
  companyId: string;
}

export default function useAiReview() {
  const dismissAiReview = useCallback(
    async ({ aiReviewId, companyId }: AiReviewDismissalArgs) => {
      try {
        const response = await putV1CompaniesCompanyIdAiReviewsIdDismiss({
          path: { 'company-id': companyId, id: aiReviewId },
        });

        return AiReview.parse(response.data.data);
      } catch (error) {
        // A re-review landing between render and click discards the review the user was
        // looking at, so this 404s. Their intent no longer applies to a review that no
        // longer exists — report it as superseded so the caller can carry on rather than
        // showing a failure they can do nothing about.
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }

        connection.handleRequestError(
          error,
          t('error_messages.ai_reviews.failed_to_dismiss'),
        );
        throw error;
      }
    },
    [],
  );

  const undismissAiReview = useCallback(
    async ({ aiReviewId, companyId }: AiReviewDismissalArgs) => {
      try {
        const response = await putV1CompaniesCompanyIdAiReviewsIdUndismiss({
          path: { 'company-id': companyId, id: aiReviewId },
        });

        return AiReview.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }

        connection.handleRequestError(
          error,
          t('error_messages.ai_reviews.failed_to_undismiss'),
        );
        throw error;
      }
    },
    [],
  );

  return { dismissAiReview, undismissAiReview };
}
